import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Timer, XCircle, Clock, Plus, Pencil, Trash2, List, FileDown } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from "recharts";
import { PageHeader, MonthYearFilter, SectionCard, StatCard, Footer } from "@/components/common";
import ExportDialog from "@/components/ExportDialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { exportToPDF } from "@/lib/pdfExport";
import {
  SAFETY_CATEGORIES, PRIORITIES, STATUSES, CHART_COLORS,
  priorityClass, statusClass, statusHex, formatDateID, todayISO, MONTHS,
} from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const emptyForm = { date: todayISO(), description: "", category: SAFETY_CATEGORIES[0], priority: "Low", due_date: todayISO(), status: "No Progress", equipment_area: "", responsibility: "" };

function FormDialog({ open, onOpenChange, initial, onSaved, defaultDate }) {
  const [form, setForm] = useState(emptyForm);
  useEffect(() => { setForm(initial ? { ...emptyForm, ...initial } : { ...emptyForm, date: defaultDate || emptyForm.date }); }, [initial, open, defaultDate]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    try {
      if (initial?.id) await api.put(`/findings/safety/${initial.id}`, form);
      else await api.post("/findings/safety", form);
      toast.success("Data tersimpan");
      onSaved();
      onOpenChange(false);
    } catch (e) { toast.error("Gagal menyimpan data"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="fs-form">
        <DialogHeader><DialogTitle>{initial?.id ? "Edit" : "Input"} Finding Safety</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div><Label className="text-xs">Date</Label><Input type="date" value={form.date?.slice(0,10)} onChange={(e)=>set("date",e.target.value)} data-testid="fs-date" /></div>
          <div><Label className="text-xs">Due Date</Label><Input type="date" value={form.due_date?.slice(0,10)} onChange={(e)=>set("due_date",e.target.value)} data-testid="fs-due" /></div>
          <div className="col-span-2"><Label className="text-xs">Description Finding Safety</Label><Textarea value={form.description} onChange={(e)=>set("description",e.target.value)} data-testid="fs-desc" /></div>
          <div className="col-span-2"><Label className="text-xs">Kategori</Label>
            <Select value={form.category} onValueChange={(v)=>set("category",v)}><SelectTrigger data-testid="fs-cat"><SelectValue/></SelectTrigger>
              <SelectContent>{SAFETY_CATEGORIES.map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label className="text-xs">Prioritas</Label>
            <Select value={form.priority} onValueChange={(v)=>set("priority",v)}><SelectTrigger data-testid="fs-prio"><SelectValue/></SelectTrigger>
              <SelectContent>{PRIORITIES.map(p=><SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label className="text-xs">Status</Label>
            <Select value={form.status} onValueChange={(v)=>set("status",v)}><SelectTrigger data-testid="fs-status"><SelectValue/></SelectTrigger>
              <SelectContent>{STATUSES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label className="text-xs">Equipment / Area</Label><Input value={form.equipment_area} onChange={(e)=>set("equipment_area",e.target.value)} data-testid="fs-equip" /></div>
          <div><Label className="text-xs">Responsibility</Label><Input value={form.responsibility} onChange={(e)=>set("responsibility",e.target.value)} data-testid="fs-resp" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>onOpenChange(false)}>Batal</Button>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={save} data-testid="fs-save">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const STATUS_ORDER = ["Complete", "In Progress", "No Progress", "Waiting Part"];

export default function FindingSafety() {
  const { canEdit } = useAuth();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [category, setCategory] = useState("all");
  const [list, setList] = useState([]);
  const [prevList, setPrevList] = useState([]);
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const perPage = 10;

  const load = async () => {
    const [cur, prev] = await Promise.all([
      api.get("/findings/safety", { params: { month, year, category } }),
      api.get("/findings/safety", { params: { month: month === 1 ? 12 : month - 1, year: month === 1 ? year - 1 : year } }),
    ]);
    setList(cur.data); setPrevList(prev.data); setPage(1);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, year, category]);

  const statusCount = (arr, s) => arr.filter((x) => x.status === s).length;
  const total = list.length;
  const completeC = statusCount(list, "Complete");
  const pctChange = (c, p) => (p === 0 ? (c > 0 ? 100 : 0) : Math.round(((c - p) / p) * 1000) / 10);

  const statusData = STATUS_ORDER.map((s) => ({ name: s, value: statusCount(list, s), color: statusHex(s) }));
  const catData = useMemo(() => {
    const m = {}; list.forEach((x) => { m[x.category] = (m[x.category] || 0) + 1; });
    return Object.entries(m).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [list]);
  const prioData = ["Low", "Medium", "High", "Urgent"].map((p) => ({ name: p, value: list.filter((x) => x.priority === p).length }));
  const prioColors = { Low: "#16a34a", Medium: "#f59e0b", High: "#dc2626", Urgent: "#7f1d1d" };

  const paged = list.slice((page - 1) * perPage, page * perPage);
  const rowOffset = (page - 1) * perPage;
  const totalPages = Math.ceil(list.length / perPage) || 1;

  const del = async (id) => { await api.delete(`/findings/safety/${id}`); toast.success("Data dihapus"); load(); };

  const thead = (
    <thead><tr className="text-left text-slate-500 border-b border-slate-200 text-xs">
      <th className="py-2 px-2">No</th><th className="px-2">Tanggal</th><th className="px-2">Deskripsi</th><th className="px-2">Kategori</th><th className="px-2">Prioritas</th><th className="px-2">Due Date</th><th className="px-2">Days Left</th><th className="px-2">Status</th><th className="px-2">Equipment / Area</th><th className="px-2">Responsibility</th><th className="px-2 text-center">Action</th>
    </tr></thead>
  );

  const renderRows = (rows, offset) => rows.map((r, i) => {
    const strike = r.status === "Complete" ? "line-through decoration-slate-400" : "";
    return (
      <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50 align-top">
        <td className={`py-2.5 px-2 ${strike}`}>{offset+i+1}</td>
        <td className={`px-2 whitespace-nowrap ${strike}`}>{formatDateID(r.date)}</td>
        <td className={`px-2 max-w-[240px] ${strike}`}>{r.description}</td>
        <td className={`px-2 ${strike}`}>{r.category}</td>
        <td className={`px-2 ${strike}`}><span className={`text-xs px-2 py-0.5 rounded font-medium ${priorityClass(r.priority)}`}>{r.priority}</span></td>
        <td className={`px-2 whitespace-nowrap ${strike}`}>{formatDateID(r.due_date)}</td>
        <td className={`px-2 font-semibold ${r.days_left<0?"text-red-500":"text-slate-600"} ${strike}`}>{r.days_left}</td>
        <td className={`px-2 ${strike}`}><span className={`text-xs font-semibold ${statusClass(r.status)}`}>{r.status}</span></td>
        <td className={`px-2 ${strike}`}>{r.equipment_area}</td>
        <td className={`px-2 ${strike}`}>{r.responsibility}</td>
        <td className="px-2 text-center whitespace-nowrap">
          {canEdit ? (<div className="flex items-center justify-center gap-1">
            <button onClick={()=>{setEditing(r);setFormOpen(true);}} className="p-1.5 rounded hover:bg-blue-50 text-blue-600" data-testid={`fs-edit-${r.id}`}><Pencil className="w-4 h-4"/></button>
            <button onClick={()=>del(r.id)} className="p-1.5 rounded hover:bg-red-50 text-red-500" data-testid={`fs-del-${r.id}`}><Trash2 className="w-4 h-4"/></button>
          </div>) : <span className="text-xs text-slate-300">-</span>}
        </td>
      </tr>
    );
  });

  return (
    <div className="p-6" id="finding-safety-page">
      <PageHeader icon={AlertTriangle} title="FINDING SAFETY" subtitle="Monitoring dan laporan temuan keselamatan kerja"
        right={<MonthYearFilter month={month} setMonth={setMonth} year={year} setYear={setYear} />} />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <StatCard icon={AlertTriangle} iconBg="#dbeafe" iconColor="#2563eb" label="Finding Safety" value={total} change={pctChange(total, prevList.length)} last={prevList.length} sparkType="bar" sparkColor="#2563eb" seed={1} />
        <StatCard icon={CheckCircle2} iconBg="#ede9fe" iconColor="#7c3aed" label="Complete" value={completeC} change={pctChange(completeC, statusCount(prevList, "Complete"))} last={statusCount(prevList, "Complete")} sparkType="bar" sparkColor="#7c3aed" seed={2} />
        <StatCard icon={Timer} iconBg="#ffedd5" iconColor="#ea580c" label="In Progress" value={statusCount(list, "In Progress")} change={0} last={statusCount(prevList, "In Progress")} sparkType="line" sparkColor="#ea580c" seed={3} />
        <StatCard icon={XCircle} iconBg="#fef9c3" iconColor="#ca8a04" label="No Progress" value={statusCount(list, "No Progress")} change={pctChange(statusCount(list,"No Progress"), statusCount(prevList,"No Progress"))} last={statusCount(prevList, "No Progress")} sparkType="bar" sparkColor="#ca8a04" seed={4} />
        <StatCard icon={Clock} iconBg="#dbeafe" iconColor="#2563eb" label="Waiting Part" value={statusCount(list, "Waiting Part")} change={0} last={statusCount(prevList, "Waiting Part")} sparkType="bar" sparkColor="#2563eb" seed={5} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
        <SectionCard title="Status Finding Safety">
          <div className="flex items-center gap-4">
            <div className="w-32 h-32 relative">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart><Pie data={statusData} dataKey="value" innerRadius={40} outerRadius={60} paddingAngle={2}>
                  {statusData.map((e, i) => <Cell key={i} fill={e.color} />)}</Pie></PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center"><span className="text-xs text-slate-400">Total</span><span className="text-2xl font-extrabold">{total}</span></div>
            </div>
            <div className="space-y-1.5 text-sm flex-1">
              {statusData.map((s) => <div key={s.name} className="flex items-center justify-between"><span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{background:s.color}} />{s.name}</span><span className="font-semibold">{s.value} ({total?Math.round(s.value/total*100):0}%)</span></div>)}
            </div>
          </div>
          <div className="mt-3"><div className="flex justify-between text-xs mb-1"><span>Completed out of {total}</span><span className="font-bold text-green-600">{total?Math.round(completeC/total*100):0}%</span></div>
            <div className="h-2 bg-slate-100 rounded-full"><div className="h-2 bg-green-500 rounded-full" style={{width:`${total?completeC/total*100:0}%`}}/></div></div>
        </SectionCard>

        <SectionCard title="Finding Safety by Category">
          <div className="flex flex-col items-center">
            <div className="w-32 h-32 relative shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart><Pie data={catData} dataKey="value" innerRadius={40} outerRadius={60} paddingAngle={1}>
                  {catData.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}</Pie></PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center"><span className="text-[10px] text-slate-400">Total</span><span className="text-xl font-extrabold">{total}</span></div>
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] w-full mt-3 max-h-24 overflow-y-auto pr-1">
              {catData.map((c, i) => <div key={c.name} className="flex items-center justify-between gap-2"><span className="flex items-center gap-1.5 truncate"><span className="w-2 h-2 rounded-full shrink-0" style={{background:CHART_COLORS[i%CHART_COLORS.length]}}/><span className="truncate">{c.name}</span></span><span className="font-semibold shrink-0">{c.value} ({total?Math.round(c.value/total*100):0}%)</span></div>)}
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Finding Safety by Priority">
          <div className="flex items-center gap-4">
            <div className="w-32 h-32 relative">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart><Pie data={prioData} dataKey="value" innerRadius={40} outerRadius={60} paddingAngle={2}>
                  {prioData.map((e) => <Cell key={e.name} fill={prioColors[e.name]} />)}</Pie></PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center"><span className="text-xs text-slate-400">Total</span><span className="text-2xl font-extrabold">{total}</span></div>
            </div>
            <div className="space-y-1.5 text-sm flex-1">
              {prioData.map((p) => <div key={p.name} className="flex items-center justify-between"><span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{background:prioColors[p.name]}}/>{p.name}</span><span className="font-semibold">{p.value} ({total?Math.round(p.value/total*100):0}%)</span></div>)}
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Today's Date">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <p className="text-xs text-slate-500">{now.toLocaleDateString("id-ID",{weekday:"long"})}</p>
              <p className="text-4xl font-extrabold text-slate-900">{String(now.getDate()).padStart(2,"0")}</p>
              <p className="text-xs font-semibold bg-blue-600 text-white rounded px-2 py-0.5 inline-block mt-1">{MONTHS[now.getMonth()].l} {now.getFullYear()}</p>
              <div className="mt-3 text-left"><p className="text-xs text-slate-400">Total Finding Safety</p><p className="text-xl font-extrabold">{total}</p><p className="text-[10px] text-slate-400">vs Last Month: {prevList.length}</p></div>
            </div>
            <div className="space-y-2">
              {statusData.map((s)=>(<div key={s.name} className="flex items-center justify-between rounded-lg px-3 py-2" style={{background:`${s.color}15`}}><span className="text-xs font-medium" style={{color:s.color}}>{s.name}</span><span className="font-bold" style={{color:s.color}}>{s.value}</span></div>))}
            </div>
          </div>
        </SectionCard>
      </div>

      <SectionCard
        title="List Finding Safety"
        right={<div className="flex items-center gap-2">
          <Select value={category} onValueChange={setCategory}><SelectTrigger className="h-8 w-48 text-xs" data-testid="fs-filter-cat"><SelectValue placeholder="Semua Kategori"/></SelectTrigger>
            <SelectContent><SelectItem value="all">Semua Kategori</SelectItem>{SAFETY_CATEGORIES.map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
          {canEdit && <Button size="sm" className="bg-blue-600 hover:bg-blue-700 h-8" onClick={()=>{setEditing(null);setFormOpen(true);}} data-testid="fs-add"><Plus className="w-4 h-4 mr-1"/>Input Data</Button>}
          <Button size="sm" variant="outline" className="h-8" onClick={()=>setExportOpen(true)} data-testid="fs-export"><FileDown className="w-4 h-4 mr-1"/>Export PDF</Button>
        </div>}
      >
        <div className="overflow-x-auto print:hidden">
          <table className="w-full text-sm" data-testid="fs-table">
            {thead}
            <tbody>
              {renderRows(paged, rowOffset)}
              {paged.length===0 && <tr><td colSpan={11} className="text-center py-8 text-slate-400">Tidak ada data untuk periode ini</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="hidden print:block overflow-x-auto" data-testid="fs-table-print">
          <table className="w-full text-sm">
            {thead}
            <tbody>
              {renderRows(list, 0)}
              {list.length===0 && <tr><td colSpan={11} className="text-center py-8 text-slate-400">Tidak ada data untuk periode ini</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between mt-4 text-sm print:hidden">
          <span className="text-slate-500">Menampilkan {list.length? (page-1)*perPage+1:0} - {Math.min(page*perPage,list.length)} dari {list.length} data</span>
          <div className="flex gap-1">
            {Array.from({length:totalPages}).slice(0,6).map((_,i)=>(<button key={i} onClick={()=>setPage(i+1)} className={`w-8 h-8 rounded text-xs font-medium ${page===i+1?"bg-blue-600 text-white":"bg-white border border-slate-200"}`}>{i+1}</button>))}
          </div>
        </div>
      </SectionCard>

      <FormDialog open={formOpen} onOpenChange={setFormOpen} initial={editing} onSaved={load} defaultDate={`${year}-${String(month).padStart(2,"0")}-15`} />
      <ExportDialog open={exportOpen} onOpenChange={setExportOpen} onExport={(o)=>exportToPDF("finding-safety-page", `Finding_Safety_${month}_${year}`, o)} />
      <Footer />
    </div>
  );
}
